import axiosInstance from "../components/api/axiosInstance";
import { deleteProjectFile } from "./projectFileService";

/**
 * Task / subtask file APIs (shared multipart endpoint):
 *
 * Subtask upload: POST /file — form-data: sub_task_id, file
 *   → e.g. { File_Id, File_Name, File_URL, Sub_Task_Id, Task_Id: null, ... }
 *
 * Subtask list: GET /files?sub_task_id=<id> — array of file rows (client filters if server returns all).
 *
 * Delete: DELETE /delete-file/<File_Id> — e.g. { File_Id, message: "Deleted" }
 *   (POST fallback on 405). Override with REACT_APP_SUBTASK_FILE_DELETE_PATH (use {id} or :id).
 */

/** Same endpoint as project/issue files */
export const deleteTaskFile = deleteProjectFile;

export async function deleteSubTaskFile(fileId) {
  const id = Number(fileId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid file id");
  }
  const customPath =
    process.env.REACT_APP_SUBTASK_FILE_DELETE_PATH ||
    process.env.REACT_APP_PROJECT_FILE_DELETE_PATH ||
    process.env.REACT_APP_ISSUE_FILE_DELETE_PATH;
  const path =
    customPath && String(customPath).trim()
      ? String(customPath).replace(/\{id\}/g, String(id)).replace(/:id/g, String(id))
      : `/delete-file/${id}`;

  try {
    const response = await axiosInstance.delete(path);
    return response.data?.data ?? response.data;
  } catch (err) {
    if (err?.response?.status === 405) {
      const response = await axiosInstance.post(path);
      return response.data?.data ?? response.data;
    }
    throw err;
  }
}

const validateStatusProbe = (status) =>
  (status >= 200 && status < 300) || status === 404 || status === 405;

/**
 * POST /create-task response → task id
 */
export function extractCreatedTaskId(created, depth = 0) {
  if (created == null || depth > 4) return null;
  const obj = Array.isArray(created) ? created[0] : created;
  if (!obj || typeof obj !== "object") return null;

  const raw =
    obj.Task_Id ??
    obj.task_id ??
    obj.TaskId ??
    obj.taskId ??
    obj.insertId ??
    obj.last_insert_id ??
    obj.ID ??
    obj.Id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;

  const inner = obj.data ?? obj.task ?? obj.result ?? obj.payload;
  if (inner != null && inner !== obj) {
    const nested = extractCreatedTaskId(inner, depth + 1);
    if (nested != null) return nested;
  }

  const lastResort = Number(obj.id);
  if (Number.isFinite(lastResort) && lastResort > 0) return lastResort;

  return null;
}

/**
 * POST /create-subtask response → subtask id
 */
export function extractCreatedSubTaskId(created, depth = 0) {
  if (created == null || depth > 4) return null;
  const obj = Array.isArray(created) ? created[0] : created;
  if (!obj || typeof obj !== "object") return null;

  const raw =
    obj.Sub_Task_Id ??
    obj.sub_task_id ??
    obj.SubTaskId ??
    obj.subTaskId ??
    obj.insertId ??
    obj.last_insert_id ??
    obj.ID ??
    obj.Id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;

  const inner = obj.data ?? obj.sub_task ?? obj.subTask ?? obj.result ?? obj.payload;
  if (inner != null && inner !== obj) {
    const nested = extractCreatedSubTaskId(inner, depth + 1);
    if (nested != null) return nested;
  }

  const lastResort = Number(obj.id);
  if (Number.isFinite(lastResort) && lastResort > 0) return lastResort;

  return null;
}

export function normalizeTaskFileRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rec = {
    fileId: raw.File_Id ?? raw.file_id,
    fileName: raw.File_Name ?? raw.file_name ?? "Attachment",
    filePath: raw.File_Path ?? raw.file_path ?? "",
    fileUrl: raw.File_URL ?? raw.file_url ?? "",
    taskId: raw.Task_Id ?? raw.task_id,
    subTaskId: raw.Sub_Task_Id ?? raw.sub_task_id,
    projectId: raw.Project_Id ?? raw.project_id,
    issueId: raw.Issue_Id ?? raw.issue_id,
    uploadedOn: raw.Uploaded_On ?? raw.uploaded_on,
    uploadedBy: raw.Uploaded_By ?? raw.uploaded_by
  };
  if (rec.fileId == null && !rec.fileUrl) return null;
  return rec;
}

function parseFilesArrayResponse(res) {
  const body = res?.data;
  const arr = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.files)
        ? body.files
        : [];
  return (arr || []).map(normalizeTaskFileRecord).filter(Boolean);
}

/**
 * Some backends ignore query params and return every file. Keep only rows for this task.
 * Task screen: same Task_Id and no Sub_Task_Id (subtasks use SubTaskFileAttachments).
 */
export function filterFilesForTask(files, taskId) {
  const id = Number(taskId);
  if (!Number.isFinite(id)) return [];
  return (files || []).filter((f) => {
    if (Number(f.taskId) !== id) return false;
    const st =
      f.subTaskId != null && f.subTaskId !== ""
        ? Number(f.subTaskId)
        : null;
    if (st != null && Number.isFinite(st) && st > 0) return false;
    return true;
  });
}

/** GET /files?sub_task_id= may still return all files — keep only this subtask's rows. */
export function filterFilesForSubTask(files, subTaskId) {
  const id = Number(subTaskId);
  if (!Number.isFinite(id)) return [];
  return (files || []).filter((f) => Number(f.subTaskId) === id);
}

export function extractTaskFilesFromPayload(task) {
  if (!task) return [];
  const raw =
    task.Files ??
    task.files ??
    task.File_List ??
    task.file_list ??
    task.Attachments ??
    task.attachments ??
    task.Task_Files ??
    task.task_files ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeTaskFileRecord).filter(Boolean);
}

export function extractSubTaskFilesFromPayload(subTask) {
  if (!subTask) return [];
  const raw =
    subTask.Files ??
    subTask.files ??
    subTask.File_List ??
    subTask.file_list ??
    subTask.Attachments ??
    subTask.attachments ??
    subTask.Sub_Task_Files ??
    subTask.sub_task_files ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeTaskFileRecord).filter(Boolean);
}

/**
 * POST /file — form: task_id, file
 */
export async function uploadTaskFile(taskId, file) {
  const id = Number(taskId);
  if (!Number.isFinite(id)) {
    throw new Error("Invalid task id");
  }
  if (!file || !(file instanceof Blob)) {
    throw new Error("No file selected");
  }
  const formData = new FormData();
  formData.append("task_id", String(id));
  formData.append("file", file);
  const response = await axiosInstance.post("/file", formData);
  const data = response.data?.data ?? response.data;
  return normalizeTaskFileRecord(data) || data;
}

/**
 * POST /file — form: sub_task_id, file
 */
export async function uploadSubTaskFile(subTaskId, file) {
  const id = Number(subTaskId);
  if (!Number.isFinite(id)) {
    throw new Error("Invalid subtask id");
  }
  if (!file || !(file instanceof Blob)) {
    throw new Error("No file selected");
  }
  const formData = new FormData();
  formData.append("sub_task_id", String(id));
  formData.append("file", file);
  const response = await axiosInstance.post("/file", formData);
  const data = response.data?.data ?? response.data;
  return normalizeTaskFileRecord(data) || data;
}

/**
 * GET /files?task_id= — some backends use typo taskt_id
 */
export async function fetchTaskFiles(taskId) {
  const id = Number(taskId);
  if (!Number.isFinite(id)) return [];

  const customPath = process.env.REACT_APP_TASK_FILES_LIST_PATH;
  const urls = [];
  if (customPath && String(customPath).trim()) {
    urls.push({ url: String(customPath).trim(), params: { task_id: id } });
  }
  // Some backends use typo param taskt_id for task file lists
  urls.push(
    { url: "/files", params: { taskt_id: id } },
    { url: "/files", params: { task_id: id } },
    { url: "/files", params: { Task_Id: id } }
  );

  for (const { url, params } of urls) {
    const res = await axiosInstance.get(url, {
      params,
      validateStatus: validateStatusProbe
    });
    if (res.status === 404 || res.status === 405) continue;
    if (res.status >= 200 && res.status < 300) {
      return filterFilesForTask(parseFilesArrayResponse(res), id);
    }
  }
  return [];
}

/**
 * GET /files?sub_task_id=
 */
export async function fetchSubTaskFiles(subTaskId) {
  const id = Number(subTaskId);
  if (!Number.isFinite(id)) return [];

  const customPath = process.env.REACT_APP_SUBTASK_FILES_LIST_PATH;
  const urls = [];
  if (customPath && String(customPath).trim()) {
    urls.push({ url: String(customPath).trim(), params: { sub_task_id: id } });
  }
  urls.push(
    { url: "/files", params: { sub_task_id: id } },
    { url: "/files", params: { Sub_Task_Id: id } }
  );

  for (const { url, params } of urls) {
    const res = await axiosInstance.get(url, {
      params,
      validateStatus: validateStatusProbe
    });
    if (res.status === 404 || res.status === 405) continue;
    if (res.status >= 200 && res.status < 300) {
      return filterFilesForSubTask(parseFilesArrayResponse(res), id);
    }
  }
  return [];
}
